import { useMediaQuery } from '@mui/material';
import { useEffect, useRef, useState } from 'react';

export const HOVER_CARD_OPEN_DELAY_MS = 260;
export const HOVER_CARD_CLOSE_DELAY_MS = 150;

export const useHoverCardDelay = () => {
  const [isOpen, setIsOpen] = useState(false);
  const isTouch = useMediaQuery('(hover: none) and (pointer: coarse)');

  // useRef, not useState: timer ids must survive re-renders without causing
  // one, and storing them in state would re-render the pill on every hover tick.
  const showTimer = useRef<number | null>(null);
  const hideTimer = useRef<number | null>(null);

  const clearTimers = () => {
    if (showTimer.current) {
      window.clearTimeout(showTimer.current);
      showTimer.current = null;
    }
    if (hideTimer.current) {
      window.clearTimeout(hideTimer.current);
      hideTimer.current = null;
    }
  };

  useEffect(() => clearTimers, []);

  const openNow = () => {
    clearTimers();
    setIsOpen(true);
  };

  const closeNow = () => {
    clearTimers();
    setIsOpen(false);
  };

  const closeSoon = () => {
    clearTimers();
    hideTimer.current = window.setTimeout(() => setIsOpen(false), HOVER_CARD_CLOSE_DELAY_MS);
  };

  // window.setTimeout, not an event handler/useEffect/flushSync: showing and
  // hiding the card is an intentional grace period so the pointer can travel
  // from the pill into the card without it flickering closed, which isn't a
  // render-time or event-time computation — tried deriving it from the
  // enter/leave events directly, but that opens/closes on every hover tick
  // instead of only after the pointer settles.
  const openSoon = () => {
    if (isTouch) {
      openNow();
      return;
    }
    clearTimers();
    showTimer.current = window.setTimeout(() => setIsOpen(true), HOVER_CARD_OPEN_DELAY_MS);
  };

  const toggle = () => setIsOpen((prev) => !prev);

  return { isOpen, openNow, closeNow, openSoon, closeSoon, toggle };
};
